import { useEffect, useRef } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import miniInHandImage from '../../../assets/images/mini-in-hand.jpg';
import Loading from '../../../components/Loading';
import {
  NanoBagFallbackReason,
  trackNanoBagCheckoutTapped,
  trackNanoBagFellBackToShop,
  trackNanoBagSkipTapped,
  trackNanoBagViewed,
} from '../../../lib/analytics/events';
import { formatCents } from '../../../lib/checkout/pricing';
import { useCheckoutOffer } from '../../../lib/checkout/useCheckoutOffer';
import { Links } from '../../../lib/constants';
import { petPronouns } from '../../../lib/petUtils';
import { appendPetParams, getShopURL } from '../../../lib/shopURL';
import { CheckMark, CircledCheck } from '../CheckMark';
import OrderSummary from '../OrderSummary/OrderSummary';
import { useRegisteredChip } from '../useRegisteredChip';
import { useScrollToStepTop } from '../useScrollToStepTop';
import styles from './BagStep.module.scss';

/**
 * The no-offer shop handoff, and the bag it used to be.
 *
 * NOT in the happy path any more. "Start tracking" now goes straight to `/register/checkout`, which
 * absorbed this screen's headline and benefit list -- the bag was a second confirmation of a decision
 * made on the screen before it, and 70% of the shoppers who reached it tapped "Not now" there.
 *
 * What it still does is own the shop fallback: `CheckoutStep` redirects here when no offer resolves,
 * so the decision and its instrumentation live in one place. That fallback is deliberately not silent
 * -- its rate is how much of the funnel the in-site flow actually covers -- and since #430 gave every
 * registrant the credit it should be close to zero. If `Bag > Fell Back To Shop` starts moving, offer
 * resolution is broken.
 *
 * The bag UI below is still reachable by navigating to /register/bag directly. It is kept rather than
 * deleted so the fallback keeps a rendered state to sit behind, and so the merge can be reverted by
 * pointing the CTA back at this route.
 */
export default function BagStep() {
  const { loading, petInfo, gwaOffer } = useRegisteredChip();
  const history = useHistory();

  const {
    resolved: offerResolved,
    offer,
    breakdown,
  } = useCheckoutOffer(gwaOffer?.shopUrl ?? null, !loading && !!petInfo);

  const ready = offerResolved && !!petInfo;
  useScrollToStepTop(ready);

  // one view event per arrival, not per render
  const viewTracked = useRef(false);
  useEffect(() => {
    if (!ready || viewTracked.current || !petInfo) {
      return;
    }
    viewTracked.current = true;
    const properties = { petId: petInfo.id, gwa: !!gwaOffer };
    if (offer && breakdown) {
      trackNanoBagViewed(properties);
      return;
    }
    const reason: NanoBagFallbackReason = offer ? 'no_price' : 'no_offer';
    trackNanoBagFellBackToShop(properties, reason);
    // the handoff is a full page navigation, so without this the reason is invisible to anyone
    // debugging why the bag "did not appear"
    console.warn(`[nano checkout] no bag, handing off to the shop: ${reason}`);
  }, [ready, petInfo, gwaOffer, offer, breakdown]);

  if (!loading && !petInfo) {
    return <Redirect to={Links.register()} />;
  }

  if (loading || !ready || !petInfo) {
    return <Loading />;
  }

  // no offer or no price means nothing to sell here honestly; hand off to the shop instead
  if (!offer || !breakdown) {
    const shopUrl = gwaOffer ? appendPetParams(gwaOffer.shopUrl, petInfo) : getShopURL('NanoBag', petInfo, 'mini');
    window.location.assign(shopUrl);
    return <Loading />;
  }

  const { possessive, subject, object } = petPronouns(petInfo.gender);
  const capitalisedPossessive = possessive.charAt(0).toUpperCase() + possessive.slice(1);
  const properties = { petId: petInfo.id, gwa: !!gwaOffer };

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        <button className={styles.back} type="button" onClick={() => history.goBack()}>
          Back
        </button>

        <div className={styles.eyebrow}>For {petInfo.name}</div>
        <h1 className={styles.title}>Add GPS tracking for {petInfo.name}.</h1>
        <p className={styles.subtitle}>
          Microchips can’t GPS track. Adding a Fi tracker is the safest thing you can do for {object}.
        </p>

        <div className={styles.card}>
          <div className={styles.productRow}>
            <img className={styles.productThumb} src={miniInHandImage} alt="Fi Mini GPS tracker" />
            <div>
              <div className={styles.productName}>Fi Mini GPS tracker</div>
              <div className={styles.productDetail}>Clips onto {possessive} collar</div>
            </div>
          </div>

          <OrderSummary breakdown={breakdown} />
        </div>

        <div className={styles.card}>
          <div className={styles.sectionTitle}>What {petInfo.name} gets</div>
          <ul className={styles.benefits}>
            <li className={styles.benefit}>
              <CircledCheck />
              <span>See where {subject} is, 24/7 live on a map</span>
            </li>
            <li className={styles.benefit}>
              <CircledCheck />
              <span>An alert the moment {subject} leaves a safe zone</span>
            </li>
            <li className={styles.benefit}>
              <CircledCheck />
              <span>{capitalisedPossessive} steps and sleep, tracked automatically</span>
            </li>
          </ul>
        </div>

        <div className={styles.afterBox}>
          <div className={styles.afterTitle}>What happens after 6 months</div>
          <p className={styles.afterBody}>
            Your{' '}
            <strong>
              {formatCents(breakdown.totalInCents)} activation covers {petInfo.name}’s first 6 months
            </strong>
            .{' '}
            {offer.renewalText ? (
              <>
                After that, membership renews at <strong>{offer.renewalText}</strong>, and we’ll email you before it
                does.{' '}
              </>
            ) : (
              <>We’ll email you before it renews. </>
            )}
            Cancel any time from the app.
          </p>
        </div>

        <div className={styles.reassurance}>
          <span className={styles.reassuranceItem}>
            <CheckMark size={12} /> Cancel anytime
          </span>
        </div>

        <div className={styles.ctaContainer}>
          <button
            className={styles.ctaPrimary}
            type="button"
            onClick={() => {
              trackNanoBagCheckoutTapped(properties);
              history.push('/register/checkout');
            }}
          >
            Check out
          </button>
          <a
            className={styles.ctaGhost}
            href={Links.manageHome}
            onClick={() => {
              trackNanoBagSkipTapped(properties);
            }}
          >
            Not now
          </a>
        </div>
      </div>
    </div>
  );
}
